import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import PageTitle from './PageTitle';
import Waiting from './Waiting';
import ErrorDisplay from './ErrorDisplay';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';

import clsx from 'clsx';
import useStyles from '../styles/Styles';

import _ from 'lodash';
import dayjs from 'dayjs';
import {isTrue, describeDate } from '../utils';

import { loadSettings, loadMembersInfo, addToCart, deleteFromCart, resetErrorMessage, loadMember, fetchEnrolments, fetchCart } from '../actions';

const MembershipCard = ({subscription}) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	
	const cart = useSelector(state => state.cart.data);
	const message = useSelector(state => state.cart.message);
	
	const [submitting, setSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState(false);
	
	const dispatchAdd = (code) => {
		setSubmitting(true);
		setErrorMessage(false);
		dispatch(resetErrorMessage());
		dispatch(addToCart(code));
	}
	
	const dispatchDelete = (code) => {
		setSubmitting(true);
		setErrorMessage(false);
		dispatch(resetErrorMessage());
		dispatch(deleteFromCart(code));
	}
	
	const dispatchReset = () => {
		setErrorMessage(false);
		dispatch(resetErrorMessage());
	}
	
	useEffect(() => {
		setSubmitting(false);
	}, [cart]);
	
	// errorMessage is not a dependency; only want to run this when submitting or message change
	useEffect(() => {
		if(submitting && !_.isEmpty(message)){
			setSubmitting(false);
			setErrorMessage(message);
		} else if(!submitting && !_.isEmpty(errorMessage) && _.isEmpty(message)){
			setErrorMessage(false);
		} // eslint-disable-next-line
	}, [submitting, message])
	
	const getActions = (st, code) => {
		switch(st){
			case 'E':
			case 'D':
				return <Typography>You are enrolled in this subscription</Typography>;
			case 'C':
				return <>
				<Button color='primary' disabled={submitting}
					onClick={e => dispatchDelete(code)}
				>Remove from cart
				{submitting && <CircularProgress size={24} className={classes.buttonProgress} />}
				</Button>
				<Link to="/checkout"><Button color="primary" variant="contained">Checkout</Button></Link>
				</>
			default:
				return <Button variant='contained' color='primary' disabled={submitting}
					onClick={e => dispatchAdd(code)}>Add to cart
				{submitting && <CircularProgress size={24} className={classes.buttonProgress} />}
				</Button>
		}
	}
	
	return <Grid item xs={12} md={6}>
		<Card elevation={2} square >
		<CardContent>
		<Typography paragraph variant='h6'>{subscription.code}: {subscription.name}</Typography>
		<Typography gutterBottom><strong>Dates:</strong> {subscription.start} - {subscription.finish}</Typography>
		<Typography><strong>Fee:</strong> ${subscription.fee}</Typography>
		</CardContent>
		<CardActions >
		{ errorMessage ? 
		<>
		<Typography>{errorMessage}</Typography>
		<Button color="primary" disabled={submitting}
			onClick={dispatchReset}
		>Dismiss
		{submitting && <CircularProgress size={24} className={classes.buttonProgress} />}
		</Button>
		</> : 
		getActions(subscription.status, subscription.code) }
		</CardActions>
		</Card>
		</Grid>
}

const BuyMembership = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const permissions = useSelector(state => state.permissions); // user_can_buy_membership, cart_available
	const memberSettings = useSelector(state => state.settings.members.data);
	const membershipTypes = useSelector(state => state.settings.membership_types.data);
	const fetchingSubs = useSelector(state => state.settings.fetching_subscriptions);
	const subscriptions = useSelector(state => state.settings.subscriptions.data);
	const member = useSelector(state => state.member.data);
	const cart = useSelector(state => state.cart.data);
	const enrolledSubs = useSelector(state => state.enrolments.data.subscriptions);
	
	useEffect(() => {
		dispatch(loadMember());
		dispatch(loadSettings('members'));
		dispatch(loadMembersInfo('membership_types'));
		dispatch(loadMembersInfo('subscriptions'));
	}, [dispatch])
	
	useEffect(() => {
		if(member.member_no > 0){
			dispatch(fetchEnrolments(dayjs().format('YYYY-MM-DD'), dayjs().add(2, 'years').format('YYYY-MM-DD')));
			dispatch(fetchCart());
		}
	}, [dispatch, member])
	
	const [filteredSubs, setFilteredSubs] = useState({});
	useEffect(() => {
		const membership = _.find(membershipTypes, ['code', member.member_type]);
		const memberFee = membership && isTrue(memberSettings.use_member_type_fee) ? _.toNumber(membership.amount) : false;
		
		const filtered = _.chain(subscriptions)
			.filter(s => {
				return !isTrue(s.is_closed);
			}).sortBy('start_date').map(s => {
				const status = _.has(enrolledSubs, s.code) ? 
					(enrolledSubs[s.code].status === 'Deleted' ? 'D' : 'E') :
					(_.some(cart, ['course_code', s.code]) ? 'C' : '');
				
				return {
					status: status,
					start: describeDate(s.start_date),
					finish: describeDate(s.end_date),
					code: s.code,
					name: s.name,
					fee: memberFee === false ? _.toNumber(s.fee) : memberFee
				}
			}).keyBy('code').value();
			
		setFilteredSubs(filtered);
	}, [memberSettings, membershipTypes, subscriptions, member, enrolledSubs, cart]);
	
	const describeCurrentMembership = () => {
		if(member.enrolled_subscription){
			const s = _.has(enrolledSubs, member.enrolled_subscription) ? enrolledSubs[member.enrolled_subscription] : false;
			
			return s && _.toNumber(s.amount_charged) > _.toNumber(s.amount_paid) ? 
				<>
				<Typography gutterBottom variant='h6' component='p' color='error'>You have an unpaid membership.</Typography>
				<Typography gutterBottom>Your invoice for membership subscription {member.enrolled_subscription} ({member.enrolled_subs_name}), expiring on {describeDate(member.enrolled_subs_expiry)}, has not been paid.</Typography>
				<Typography paragraph>
				<Link to="/member/invoices"><Button color="primary" variant="contained">View Invoices</Button></Link>
				</Typography>
				</> : 
				<>
				<Typography gutterBottom variant='h6' component='p'>Your membership is current.</Typography>
				<Typography paragraph>You have a current membership subscription: {member.enrolled_subscription} ({member.enrolled_subs_name}), expiring on {describeDate(member.enrolled_subs_expiry)}.</Typography>
				</>
		} else if(member.membership_expiry_date){
			return <>
			<Typography gutterBottom variant='h6' component='p'>Membership expired</Typography>
			<Typography paragraph>Your previous membership subscription expired on: {describeDate(member.membership_expiry_date)}.</Typography>
			</>
		} else {
			return <Typography paragraph variant='h6' component='p'>You do not have a current membership subscription.</Typography>
		}
	}
	
	return (<>
	<ErrorDisplay />
	<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
	<PageTitle title='Pay Membership' />
	{ describeCurrentMembership() }
	<Divider />
	<Grid container spacing={2} className={classes.shopCardGrid}>
	{permissions.user_can_buy_membership ? 
	<>
	{_.isEmpty(filteredSubs) ? 
	<Grid item xs={12}>{ fetchingSubs ? 
		<Waiting message='Fetching membership subscriptions. Please wait.' /> : 
		<Typography paragraph variant='h6' component='p'>No membership subscriptions available</Typography> 
	}</Grid> : 
	_.map(filteredSubs, (s, k) => {
		return <MembershipCard subscription={s} key={k} />
	})}
	</> :
	<Grid item xs={12}>
	<Typography gutterBottom variant='h6' component='p'>Pay Membership is disabled.</Typography>
	<Typography>{permissions.user_can_enrol ? 
	<>Sorry, buying a membership separately from enrolments is currently not allowed. An appropriate membership will be added to your cart when enrolling in courses and activities, if required.</> : 
	<>Sorry, buying a membership is currently not allowed.</>}
	</Typography>
	</Grid>
	}
	</Grid>
	</Paper>
	</>
)};

export default BuyMembership